import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Upload, ChevronLeft, Eye, X } from 'lucide-react';
import PhotoUploadDialog from './PhotoUploadDialog';
import { photoAPI, categoryAPI } from '../../services/api';
import { CategoryLoadingBar, ContentLoadingBar, SearchLoadingBar } from './SidebarLoadingBar';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../../utils/adminUtils';


const PhotosSidebar = ({ isOpen, onClose, onPhotoSelect, onOpenDialog, isDialog = false, dialogSearchQuery = '', viewMode = 'grid' }) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [selectedRootCategory, setSelectedRootCategory] = useState('all');
  const [selectedChildCategory, setSelectedChildCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingChildCategories, setLoadingChildCategories] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', color: '#10B981' });
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Get auth token
  const getAuthToken = () => {
    const token = localStorage.getItem('token');
    if (token) {
      return token;
    }
    // For development, use a consistent dev token
    const devToken = 'dev-token-12345';
    localStorage.setItem('token', devToken);
    return devToken;
  };

  // Fetch photos with pagination
  const fetchPhotos = async (resetPhotos = true, pageNum = 1) => {
    if (resetPhotos) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = {
        page: pageNum,
        limit: 100, // Load 20 photos per page
      };

      // Add category filter
      if (selectedChildCategory !== 'all') {
        params.category = selectedChildCategory;
      } else if (selectedRootCategory && selectedRootCategory !== 'all') {
        params.category = selectedRootCategory;
      }

      // Add search filter
      if (effectiveSearchQuery) {
        params.search = effectiveSearchQuery;
      }

      const resp = await photoAPI.getAll(params);
      const data = resp.data;

      if (data.success) {
        const photosData = Array.isArray(data.data) ? data.data : data.data.photos || [];

        if (resetPhotos) {
          setPhotos(photosData);
        } else {
          setPhotos(prev => [...prev, ...photosData]);
        }

        // Check if there are more photos to load
        setHasMore(photosData.length === params.limit);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      if (resetPhotos) {
        setPhotos([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Fetch root categories (parentId is null)
  const fetchRootCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await photoAPI.getRootCategories();
      const data = response.data;

      if (data.success) {
        const rootCategoriesData = data.data || [];
        // Sort root categories by name
        rootCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setRootCategories(rootCategoriesData);
        // Keep "All Categories" selected by default (no auto-selection of the first category)
      }
    } catch (error) {
      console.error('Error fetching root categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Fetch child categories for selected root category
  const fetchChildCategories = async (parentId) => {
    try {
      if (!parentId || parentId === 'all') {
        setChildCategories([]);
        return;
      }

      setLoadingChildCategories(true);
      const response = await photoAPI.getChildCategories(parentId);
      const data = response.data;

      if (data.success) {
        const childCategoriesData = data.data || [];
        // Sort child categories by name
        childCategoriesData.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase();
          const nameB = String(b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setChildCategories(childCategoriesData);
      }
    } catch (error) {
      console.error('Error fetching child categories:', error);
      setChildCategories([]);
    } finally {
      setLoadingChildCategories(false);
    }
  };

  // Load more photos
  const loadMorePhotos = () => {
    if (!loadingMore && hasMore) {
      fetchPhotos(false, page + 1);
    }
  };

  // Handle upload completion
  const handleUploadComplete = () => {
    fetchPhotos(true, 1); // Refresh the photos list
  };

  // Handle root category selection
  const handleRootCategorySelect = (category_id) => {
    setSelectedRootCategory(category_id);
    setSelectedChildCategory('all'); // Reset child category selection
    setPage(1); // Reset pagination

    // Fetch child categories for the selected root category
    if (category_id && category_id !== 'all') {
      fetchChildCategories(category_id);
    } else {
      setChildCategories([]);
    }
  };

  // Handle child category selection
  const handleChildCategorySelect = (category_id) => {
    setSelectedChildCategory(category_id);
    setPage(1); // Reset pagination
  };

  // Create category
  const createCategory = async () => {
    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(ADMIN_ACTIONS.CREATE_CATEGORY);
      return;
    }

    try {
      await categoryAPI.create({ ...newCategory, type: 'photo' });
      setNewCategory({ name: '', description: '', color: '#10B981' });
      fetchRootCategories();

      // Show success notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'success',
          message: 'Photo category created successfully!'
        }
      }));
    } catch (error) {
      console.error('Error creating category:', error);

      // Show error notification
      window.dispatchEvent(new CustomEvent('app:notify', {
        detail: {
          type: 'error',
          message: error.response?.data?.message || 'Failed to create category. Please try again.'
        }
      }));
    }
  };

  // Update category
  const updateCategory = async (id, data) => {
    try {
      await categoryAPI.update(id, data);
      setEditingCategory(null);
      fetchRootCategories();
      // Refresh child categories if we're viewing them
      if (selectedRootCategory && selectedRootCategory !== 'all') {
        fetchChildCategories(selectedRootCategory);
      }
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Delete category
  const deleteCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await categoryAPI.delete(id);
        fetchRootCategories();
        // Refresh child categories if we're viewing them
        if (selectedRootCategory && selectedRootCategory !== 'all') {
          fetchChildCategories(selectedRootCategory);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };



  // Load data when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchRootCategories();
    }
  }, [isOpen]);

  // Load child categories when root category changes
  useEffect(() => {
    if (selectedRootCategory && selectedRootCategory !== 'all') {
      fetchChildCategories(selectedRootCategory);
    } else {
      setChildCategories([]);
    }
  }, [selectedRootCategory]);

  // Use dialog search query when in dialog mode
  const effectiveSearchQuery = isDialog ? dialogSearchQuery : searchQuery;

  // Load photos when filters change
  useEffect(() => {
    if (isOpen || isDialog) {
      fetchPhotos(true, 1); // Reset photos and start from page 1
    }
  }, [isOpen, isDialog, selectedRootCategory, selectedChildCategory, effectiveSearchQuery]);

  if (!isOpen && !isDialog) return null;

  return (
    <>
      {/* Custom CSS for hiding scrollbars */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className={isDialog 
        ? "h-full w-full bg-white flex flex-col" 
        : "fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col"
      }>
      {/* Header */}
      {!isDialog && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 
            className="text-lg font-semibold cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => onOpenDialog && onOpenDialog()}
            title="Click to open in large dialog"
          >
            Photos
          </h2>
        </div>
        <div className="flex space-x-2">
          <button
            className="p-2 hover:bg-gray-100 rounded cursor-pointer"
            title="Upload Photos"
            onClick={() => setShowUploadDialog(true)}
          >
            <Upload className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowCategoryManager(!showCategoryManager)}
            className="p-2 hover:bg-gray-100 rounded"
            title="Manage Categories"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>
      </div>
      )}

      {/* Search */}
      {!isDialog && (
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>
      )}

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto">

      {/* Category Manager */}
      {showCategoryManager && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h3 className="font-medium mb-3">Manage Categories</h3>

          {/* Add New Category */}
          <div className="space-y-2 mb-4">
            <input
              type="text"
              placeholder="Category name"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
            <div className="flex space-x-2">
              <input
                type="color"
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="w-10 h-8 border border-gray-300 rounded"
              />
              <button
                onClick={createCategory}
                disabled={!newCategory.name}
                className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* Existing Categories */}
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {rootCategories.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-2 bg-white rounded border">
                {editingCategory === category.id ? (
                  <div className="flex-1 space-y-1">
                    <input
                      type="text"
                      defaultValue={category.name}
                      onBlur={(e) => updateCategory(category.id, { name: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 flex-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-sm">{category.name}</span>
                  </div>
                )}
                <div className="flex space-x-1">
                  <button
                    onClick={() => setEditingCategory(editingCategory === category.id ? null : category.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}

            {/* Child Categories */}
            {childCategories.length > 0 && (
              <>
                <div className="text-xs font-medium text-gray-500 mt-4 mb-2">Child Categories</div>
                {childCategories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-2 bg-gray-50 rounded border ml-4">
                    {editingCategory === category.id ? (
                      <div className="flex-1 space-y-1">
                        <input
                          type="text"
                          defaultValue={category.name}
                          onBlur={(e) => updateCategory(category.id, { name: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 flex-1">
                        <div
                          className="w-2 h-2 rounded"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-sm">{category.name}</span>
                      </div>
                    )}
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setEditingCategory(editingCategory === category.id ? null : category.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => deleteCategory(category.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Category Dropdowns */}
      <div className="border-b border-gray-200 bg-gray-50">
        {/* Upload button for dialog mode (the sidebar header is hidden there). */}
        {isDialog && (
          <div className="px-4 pt-3 flex justify-end">
            <button
              onClick={() => setShowUploadDialog(true)}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              title="Upload a new photo"
            >
              <Upload className="w-4 h-4" /> Upload
            </button>
          </div>
        )}
        {/* Root Categories Dropdown */}
        <div className="px-4 py-3">
          {loadingCategories ? (
            <CategoryLoadingBar isLoading={loadingCategories} message="Loading categories..." />
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedRootCategory || 'all'}
                onChange={(e) => handleRootCategorySelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
              >
                <option value="all">All Categories</option>
                {rootCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Child Categories Dropdown */}
        {childCategories.length > 0 && (
          <div className="px-4 pb-3">
            {loadingChildCategories ? (
              <CategoryLoadingBar isLoading={loadingChildCategories} message="Loading subcategories..." />
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Subcategory</label>
                <select
                  value={selectedChildCategory}
                  onChange={(e) => handleChildCategorySelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm bg-white"
                >
                  <option value="all">All Subcategories</option>
                  {childCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>



        {/* Photos Grid */}
        <div className="p-4 flex-1 overflow-y-auto">
          {loading ? (
            <ContentLoadingBar isLoading={loading} message="Loading photos..." />
          ) : (
            <>
              <div className={`grid gap-2 ${isDialog ? 'grid-cols-6' : 'grid-cols-3'}`}>
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    onClick={() => onPhotoSelect(photo)}
                    className="cursor-pointer group relative"
                    title={photo.originalName || photo.name || 'photo'}
                  >
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-1 group-hover:shadow-md transition-shadow relative">
                      <img
                        src={photo.thumbnailUrl || photo.storageUrl || photo.url}
                        alt={photo.name || photo.originalName || 'photo'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Dimensions overlay */}
                      {(photo.width && photo.height) && (
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded backdrop-blur-sm">
                          {photo.width}×{photo.height}
                        </div>
                      )}
                      {/* Preview eye icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewPhoto(photo);
                        }}
                        className="absolute bottom-1 left-1 bg-black/60 hover:bg-black/80 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Preview image"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                    </div>
                    <h3 className="text-xs font-medium text-gray-900 truncate">{photo.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{photo.category}</p>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && photos.length > 0 && (
                <div className="mt-6 text-center">
                  <button
                    onClick={loadMorePhotos}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Load More Photos</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {!loading && photos.length === 0 && (
            <div className="text-center text-gray-500 mt-8">
              <p>No photos found</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search or category filter</p>
              )}
              {selectedChildCategory !== 'all' && (
                <p className="text-sm mt-1">Try selecting a different category</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Photo Preview Overlay */}
      {previewPhoto && (
        <div className="fixed inset-0 bg-black/90 z-[9999] flex flex-col items-center justify-center" onClick={() => setPreviewPhoto(null)}>
          {/* Close button */}
          <button
            onClick={() => setPreviewPhoto(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
            title="Close preview"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center p-8 max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={previewPhoto.storageUrl || previewPhoto.url || previewPhoto.thumbnailUrl}
              alt={previewPhoto.originalName || previewPhoto.name || 'preview'}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Bottom bar with info and add button */}
          <div className="w-full bg-black/50 backdrop-blur-sm px-6 py-4 flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
            <div className="text-white">
              <p className="text-sm font-medium">{previewPhoto.originalName || previewPhoto.name || 'Untitled'}</p>
              {(previewPhoto.width && previewPhoto.height) && (
                <p className="text-xs text-gray-300">{previewPhoto.width} × {previewPhoto.height}</p>
              )}
            </div>
            <button
              onClick={() => {
                onPhotoSelect(previewPhoto);
                setPreviewPhoto(null);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add to Canvas
            </button>
          </div>
        </div>
      )}

      {/* Photo Upload Dialog */}
      <PhotoUploadDialog
        isOpen={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
    </>
  );
};

export default PhotosSidebar;
